import React, { useState, useEffect } from 'react'
import { Search, Eye, Calendar, Phone, Mail, MapPin, User, FileText, BookOpen, Filter, CheckCircle, AlertCircle, Clock, XCircle, Edit, Save, X, Check, Users, Trash2, Star } from 'lucide-react'
import useAdmissionStore from '../../stores/admissionStore'

const Admissions = () => {
  // Get store state and actions
  const {
    applications,
    getFilteredApplications,
    updateApplicationStatus,
    bulkUpdateStatus,
    getStatistics,
    initializeSampleData,
    deleteApplication
  } = useAdmissionStore()

  // Initialize sample data if none exists
  useEffect(() => {
    initializeSampleData()
  }, [initializeSampleData])

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [gradeFilter, setGradeFilter] = useState('all')
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [editingStatus, setEditingStatus] = useState(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusToUpdate, setStatusToUpdate] = useState('')
  const [applicationToUpdate, setApplicationToUpdate] = useState(null)
  const [selectedApplications, setSelectedApplications] = useState([])
  const [showBulkUpdate, setShowBulkUpdate] = useState(false)
  const [bulkStatus, setBulkStatus] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [applicationToDelete, setApplicationToDelete] = useState(null)

  // Get filtered applications
  const filteredApplications = getFilteredApplications(searchTerm, statusFilter, gradeFilter)
  
  // Get statistics
  const statistics = getStatistics()

  const statuses = ['all', 'Pending', 'Under Review', 'Shortlisted', 'Accepted', 'Rejected', 'Waitlisted']
  const grades = ['all', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10']

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'Under Review':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'Shortlisted':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'Accepted':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'Rejected':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'Waitlisted':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending':
        return <Clock className="w-3 h-3" />
      case 'Under Review':
        return <AlertCircle className="w-3 h-3" />
      case 'Shortlisted':
        return <Star className="w-3 h-3" />
      case 'Accepted':
        return <CheckCircle className="w-3 h-3" />
      case 'Rejected':
        return <XCircle className="w-3 h-3" />
      case 'Waitlisted':
        return <Clock className="w-3 h-3" />
      default:
        return <Clock className="w-3 h-3" />
    }
  }

  const handleStatusUpdate = (applicationId, newStatus) => {
    setApplicationToUpdate(applicationId)
    setStatusToUpdate(newStatus)
    setShowStatusModal(true)
  }

  const confirmStatusUpdate = async () => {
    // Update status using store action
    const result = await updateApplicationStatus(applicationToUpdate, statusToUpdate, `Status updated to ${statusToUpdate}`)
    
    if (result.success) {
      // Update selectedApplication if it's the one being updated
      if (selectedApplication?._id === applicationToUpdate) {
        const updatedApp = applications.find(app => app._id === applicationToUpdate)
        if (updatedApp) {
          setSelectedApplication(updatedApp)
        }
      }
      
      setShowStatusModal(false)
      setApplicationToUpdate(null)
      setStatusToUpdate('')
      setEditingStatus(null)
      
      // Show success message
      alert(`Status updated to ${statusToUpdate} successfully!`)
    } else {
      alert(result.message || 'Failed to update status')
    }
  }

  const handleSelectApplication = (applicationId) => {
    setSelectedApplications(prev => 
      prev.includes(applicationId) 
        ? prev.filter(id => id !== applicationId)
        : [...prev, applicationId]
    )
  }

  const handleSelectAll = () => {
    if (selectedApplications.length === filteredApplications.length) {
      setSelectedApplications([])
    } else {
      setSelectedApplications(filteredApplications.map(app => app._id))
    }
  }

  const handleBulkStatusUpdate = () => {
    setShowBulkUpdate(true)
  }

  const confirmBulkUpdate = async () => {
    // Update multiple statuses using store action
    const result = await bulkUpdateStatus(selectedApplications, bulkStatus, `Bulk update to ${bulkStatus}`)
    
    if (result.success) {
      // Update selectedApplication if it's in the bulk update
      if (selectedApplication && selectedApplications.includes(selectedApplication._id)) {
        const updatedApp = applications.find(app => app._id === selectedApplication._id)
        if (updatedApp) {
          setSelectedApplication(updatedApp)
        }
      }
      
      setShowBulkUpdate(false)
      setBulkStatus('')
      setSelectedApplications([])
      
      // Show success message
      alert(`${selectedApplications.length} applications updated to ${bulkStatus} successfully!`)
    } else {
      alert(result.message || 'Failed to update applications')
    }
  }

  const handleDeleteClick = (application) => {
    setApplicationToDelete(application)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!applicationToDelete) return
    
    const result = await deleteApplication(applicationToDelete._id)
    
    if (result.success) {
      alert('Application deleted successfully!')
      setShowDeleteModal(false)
      setApplicationToDelete(null)
      // Close details modal if the deleted app was being viewed
      if (selectedApplication?._id === applicationToDelete._id) {
        setShowDetailsModal(false)
        setSelectedApplication(null)
      }
    } else {
      alert(result.message || 'Failed to delete application')
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admission Applications</h1>
          <p className="text-gray-600 mt-1">View submitted admission forms</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          {selectedApplications.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {selectedApplications.length} selected
              </span>
              <button
                onClick={handleBulkStatusUpdate}
                className="flex items-center gap-1 px-3 py-2 bg-primary-purple text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
              >
                <Users className="w-4 h-4" />
                Update Status
              </button>
              <button
                onClick={() => setSelectedApplications([])}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Clear
              </button>
            </div>
          )}
          <div className="bg-white px-4 py-2 rounded-lg border border-gray-200 text-sm">
            <span className="text-gray-600">Total Applications: </span>
            <span className="font-semibold text-gray-900">{statistics.total}</span>
          </div>
        </div>
      </div>

      {/* Simple Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Select All Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-primary-purple focus:ring-primary-purple border-gray-300 rounded"
            />
            <label htmlFor="selectAll" className="text-sm text-gray-700">
              Select All
            </label>
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="search"
              placeholder="Search by name, phone, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
            >
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Status' : status}
                </option>
              ))}
            </select>
          </div>

          {/* Grade Filter */}
          <select
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-purple focus:border-primary-purple"
          >
            {grades.map(grade => (
              <option key={grade} value={grade}>
                {grade === 'all' ? 'All Grades' : grade}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Applications Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredApplications.map((application) => (
          <div key={application._id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            {/* Selection Checkbox */}
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                checked={selectedApplications.includes(application._id)}
                onChange={() => handleSelectApplication(application._id)}
                className="mt-1 h-4 w-4 text-primary-purple focus:ring-primary-purple border-gray-300 rounded"
              />
              <div className="flex-1">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-primary-purple text-white flex items-center justify-center font-medium text-lg">
                      {application.studentName.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{application.studentName}</h3>
                      <p className="text-sm text-gray-600">{application.gradeAppliedFor || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {editingStatus === application._id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={statusToUpdate}
                          onChange={(e) => setStatusToUpdate(e.target.value)}
                          className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-purple"
                        >
                          <option value="">Select Status</option>
                          <option value="Pending">Pending</option>
                          <option value="Under Review">Under Review</option>
                          <option value="Shortlisted">Shortlisted</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Waitlisted">Waitlisted</option>
                        </select>
                        <button
                          onClick={() => handleStatusUpdate(application._id, statusToUpdate)}
                          disabled={!statusToUpdate}
                          className="p-1 text-green-600 hover:text-green-700 disabled:text-gray-400"
                        >
                          <Save className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingStatus(null)
                            setStatusToUpdate('')
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(application.status)}`}>
                          {getStatusIcon(application.status)}
                          {application.status}
                        </span>
                        <button
                          onClick={() => {
                            setEditingStatus(application._id)
                            setStatusToUpdate(application.status)
                          }}
                          className="p-1 text-gray-400 hover:text-primary-purple"
                          title="Change Status"
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Student Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    DOB: {new Date(application.dateOfBirth).toLocaleDateString()}
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-2 mt-0.5" />
                    <span>
                      {application.address ? 
                        `${application.address.street || ''}, ${application.address.city || ''}, ${application.address.state || ''} - ${application.address.pincode || ''}` 
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Parents Info */}
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Parents Information</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>Father: {application.fatherName || 'N/A'}</div>
                    <div>Mother: {application.motherName || 'N/A'}</div>
                    <div className="flex items-center">
                      <Phone className="w-3 h-3 mr-2" />
                      {application.fatherPhone || application.motherPhone || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* View Details Button */}
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => {
                      setSelectedApplication(application)
                      setShowDetailsModal(true)
                    }}
                    className="flex-1 bg-primary-purple text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(application)}
                    className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center justify-center"
                    title="Delete Application"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Application Date */}
                <div className="mt-3 text-xs text-gray-500 text-center">
                  Applied on {new Date(application.appliedDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredApplications.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No applications found</div>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-yellow-600">
            {statistics.pending}
          </div>
          <div className="text-sm text-gray-600">Pending Review</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">
            {statistics.underReview}
          </div>
          <div className="text-sm text-gray-600">Under Review</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {statistics.approved}
          </div>
          <div className="text-sm text-gray-600">Accepted</div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-2xl font-bold text-red-600">
            {statistics.rejected}
          </div>
          <div className="text-sm text-gray-600">Rejected</div>
        </div>
      </div>

      {/* Application Details Modal - View Only */}
      {showDetailsModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Application Details
                </h2>
                <p className="text-gray-600">
                  {selectedApplication.studentName} - {selectedApplication.gradeAppliedFor || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Student Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Student Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Full Name:</span>
                      <span className="font-medium">{selectedApplication.studentName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date of Birth:</span>
                      <span className="font-medium">
                        {selectedApplication.dateOfBirth 
                          ? new Date(selectedApplication.dateOfBirth).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Grade Applied For:</span>
                      <span className="font-medium">{selectedApplication.gradeAppliedFor || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Blood Group:</span>
                      <span className="font-medium">{selectedApplication.bloodGroup || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Aadhar Number:</span>
                      <span className="font-medium">{selectedApplication.aadharNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Previous School:</span>
                      <span className="font-medium">{selectedApplication.previousSchool || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Previous Grade:</span>
                      <span className="font-medium">{selectedApplication.previousGrade || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Academic Year:</span>
                      <span className="font-medium">{selectedApplication.academicYear || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Family Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Father's Name:</span>
                      <span className="font-medium">{selectedApplication.fatherName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Father's Occupation:</span>
                      <span className="font-medium">{selectedApplication.fatherOccupation || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Father's Phone:</span>
                      <span className="font-medium">{selectedApplication.fatherPhone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Father's Email:</span>
                      <span className="font-medium">{selectedApplication.fatherEmail || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mother's Name:</span>
                      <span className="font-medium">{selectedApplication.motherName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mother's Occupation:</span>
                      <span className="font-medium">{selectedApplication.motherOccupation || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mother's Phone:</span>
                      <span className="font-medium">{selectedApplication.motherPhone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Address:</span>
                      <span className="font-medium">
                        {selectedApplication.address ? 
                          `${selectedApplication.address.street || ''}, ${selectedApplication.address.city || ''}, ${selectedApplication.address.state || ''} - ${selectedApplication.address.pincode || ''}` 
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status History */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Status History</h3>
                <div className="space-y-3">
                  {selectedApplication.statusHistory.map((entry, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(entry.status)}`}>
                        {getStatusIcon(entry.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">{entry.status}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(entry.changedAt).toLocaleDateString()} at {new Date(entry.changedAt).toLocaleTimeString()}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>
                        <p className="text-xs text-gray-500 mt-1">Changed by: {entry.changedBy}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(selectedApplication.status)}`}>
                    {getStatusIcon(selectedApplication.status)}
                    {selectedApplication.status}
                  </span>
                  <button
                    onClick={() => {
                      setEditingStatus(selectedApplication._id)
                      setStatusToUpdate(selectedApplication.status)
                    }}
                    className="p-2 text-gray-400 hover:text-primary-purple border border-gray-300 rounded-lg"
                    title="Change Status"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-sm text-gray-600">
                  Applied on {new Date(selectedApplication.appliedDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {editingStatus === selectedApplication._id && (
                  <>
                    <select
                      value={statusToUpdate}
                      onChange={(e) => setStatusToUpdate(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-purple"
                    >
                      <option value="">Select Status</option>
                      <option value="Pending">Pending</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Shortlisted">Shortlisted</option>
                      <option value="Accepted">Accepted</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Waitlisted">Waitlisted</option>
                    </select>
                    <button
                      onClick={() => handleStatusUpdate(selectedApplication._id, statusToUpdate)}
                      disabled={!statusToUpdate}
                      className="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => {
                        setEditingStatus(null)
                        setStatusToUpdate('')
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDeleteClick(selectedApplication)}
                  className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Status Update</h3>
              <button
                onClick={() => setShowBulkUpdate(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Update status for {selectedApplications.length} selected applications:
              </p>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-purple"
              >
                <option value="">Select New Status</option>
                <option value="Pending">Pending</option>
                <option value="Under Review">Under Review</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
                <option value="Waitlisted">Waitlisted</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowBulkUpdate(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkUpdate}
                disabled={!bulkStatus}
                className="flex-1 px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300"
              >
                Update All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Status Update</h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-3">
                Are you sure you want to update the application status to:
              </p>
              <div className="flex items-center justify-center">
                <span className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border ${getStatusColor(statusToUpdate)}`}>
                  {getStatusIcon(statusToUpdate)}
                  {statusToUpdate}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusUpdate}
                className="flex-1 px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Confirm Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && applicationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600">Delete Application</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setApplicationToDelete(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-3">
                Are you sure you want to delete this application?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900">{applicationToDelete.studentName}</p>
                <p className="text-sm text-gray-600">Application #: {applicationToDelete.applicationNumber}</p>
                <p className="text-sm text-gray-600">Grade: {applicationToDelete.gradeAppliedFor}</p>
              </div>
              <p className="text-red-600 text-sm mt-3 font-medium">
                ⚠️ This action cannot be undone!
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setApplicationToDelete(null)
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admissions